import React from 'react';
import { X } from 'lucide-react';

const SmsTemplateModal = ({ sms }) => {
  const {
    showAddSmsTemplateModal, closeSmsTemplateModal, editingSmsTemplate,
    smsTemplateForm, setSmsTemplateForm, handleSmsTemplateSubmit,
    smsTypes, smsCategories, smsFields, insertSmsField,
  } = sms;

  if (!showAddSmsTemplateModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800">
              {editingSmsTemplate ? 'Edit SMS Template' : 'Create SMS Template'}
            </h3>
            <button onClick={closeSmsTemplateModal} className="text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSmsTemplateSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Template Form */}
              <div className="lg:col-span-2 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                    <input
                      type="text"
                      value={smsTemplateForm.name}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      placeholder="e.g., Order Confirmation"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SMS Type *</label>
                    <select
                      value={smsTemplateForm.type}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      required
                    >
                      <option value="">Select SMS Type</option>
                      {smsTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={smsTemplateForm.description}
                    onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Brief description of this template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMS Template *</label>
                  <textarea
                    value={smsTemplateForm.template}
                    onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, template: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Enter your SMS template. Use {{fieldName}} for dynamic fields."
                    required
                  />
                  <div className="mt-2 text-sm text-gray-500">
                    Characters: {smsTemplateForm.template.length} / {smsTemplateForm.maxLength}
                    {smsTemplateForm.template.length > smsTemplateForm.maxLength && (
                      <span className="text-red-500 ml-2">⚠️ Exceeds SMS limit</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={smsTemplateForm.category}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    >
                      <option value="">Select Category</option>
                      {smsCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Length</label>
                    <input
                      type="number"
                      value={smsTemplateForm.maxLength}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, maxLength: parseInt(e.target.value, 10) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      min="1"
                      max="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select
                      value={smsTemplateForm.priority}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, priority: parseInt(e.target.value, 10) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    >
                      <option value={1}>High (1)</option>
                      <option value={2}>Medium (2)</option>
                      <option value={3}>Low (3)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Send Time</label>
                    <input
                      type="text"
                      value={smsTemplateForm.sendTime}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, sendTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                      placeholder="e.g., 09:00-17:00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                    <select
                      value={smsTemplateForm.frequency}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, frequency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    >
                      <option value="immediate">Immediate</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={smsTemplateForm.isDefault}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, isDefault: e.target.checked })}
                      className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Set as default template</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={smsTemplateForm.isActive}
                      onChange={(e) => setSmsTemplateForm({ ...smsTemplateForm, isActive: e.target.checked })}
                      className="h-4 w-4 text-[#d44100] focus:ring-[#d44100] border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Right Column - Dynamic Fields */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Available Fields</h4>
                  <p className="text-sm text-gray-600 mb-4">Click on any field to insert it into your template</p>

                  {Object.keys(smsFields).map((category) => (
                    <div key={category} className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2 capitalize">{category} Fields</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {smsFields[category].map((field) => (
                          <button
                            key={field.field}
                            type="button"
                            onClick={() => insertSmsField(field.field)}
                            className="text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                            title={`${field.label} (${field.type}) - Example: ${field.example}`}
                          >
                            <div className="font-medium text-gray-900">{field.label}</div>
                            <div className="text-xs text-gray-500">{field.field}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Template Preview */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Template Preview</h4>
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <div className="text-sm text-gray-600 mb-2">Preview:</div>
                    <div className="bg-white p-3 rounded border text-sm">
                      {smsTemplateForm.template || 'Your template will appear here...'}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Character count: {smsTemplateForm.template.length}
                      {smsTemplateForm.template.length > 160 && (
                        <span className="text-orange-500 ml-2">
                          (Will be split into {Math.ceil(smsTemplateForm.template.length / 160)} SMS)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button type="button" onClick={closeSmsTemplateModal} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]">
                {editingSmsTemplate ? 'Update SMS Template' : 'Create SMS Template'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SmsTemplateModal;
